/**
 * Resize/compress capture data URLs before detect-skin to cut upload and decode time.
 */

const DEFAULT_MAX_SIDE = 1280;
const DEFAULT_QUALITY = 0.85;

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for compression'));
    img.src = dataUrl;
  });
}

/**
 * @param {string} dataUrl
 * @param {{ maxSide?: number, quality?: number }} [options]
 * @returns {Promise<string>}
 */
export async function compressDataUrl(dataUrl, options = {}) {
  if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) {
    return dataUrl;
  }

  const forAnalysis = options.forAnalysis === true;
  const maxSide = Number.isFinite(options.maxSide) ? options.maxSide : DEFAULT_MAX_SIDE;
  const quality = Number.isFinite(options.quality)
    ? options.quality
    : (forAnalysis ? 0.94 : DEFAULT_QUALITY);

  try {
    const img = await loadImage(dataUrl);
    const longest = Math.max(img.width, img.height);
    const sizeLimit = forAnalysis ? 2_500_000 : 900_000;
    if (longest <= maxSide && dataUrl.length < sizeLimit) {
      return dataUrl;
    }

    const scale = longest > maxSide ? maxSide / longest : 1;
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;
    ctx.imageSmoothingEnabled = !forAnalysis;
    if (!forAnalysis) {
      ctx.imageSmoothingQuality = 'high';
    }
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', quality);
  } catch {
    return dataUrl;
  }
}

/**
 * @param {string} frontImage
 * @param {{ left?: string, right?: string }|null|undefined} sideCaptures
 * @param {{ maxSide?: number, quality?: number }} [options]
 */
export async function compressDetectImages(frontImage, sideCaptures, options = {}) {
  const compressedFront = await compressDataUrl(frontImage, options);
  if (!sideCaptures || typeof sideCaptures !== 'object') {
    return { frontImage: compressedFront, sideCaptures: null };
  }

  const out = { ...sideCaptures };
  for (const key of ['left', 'right']) {
    if (typeof out[key] === 'string' && out[key]) {
      out[key] = await compressDataUrl(out[key], options);
    }
  }
  const hasSide = out.left || out.right;
  return {
    frontImage: compressedFront,
    sideCaptures: hasSide ? out : null,
  };
}
